import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Shield, Calendar, Building2, User, ClipboardList, StickyNote, ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PublicQrPageHeader } from '../features/electrical-systems/shared/PublicQrPageHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  return d;
};

const fmtKva = (v) => v != null ? v.toFixed(1) : '-';

const SUMMARY_FIELDS = [
  { key: 'name', label: 'Panel Name' },
  { key: 'panelType', label: 'Type' },
  { key: 'location', label: 'Location' },
  { key: 'manufacturerName', label: 'Manufacturer' },
  { key: 'model', label: 'Model' },
  { key: 'phase', label: 'Phase' },
  { key: 'busRating', label: 'Bus Rating' },
  { key: 'voltage', label: 'Voltage' },
  { key: 'mainBreaker', label: 'Main Breaker' },
  { key: 'installDate', label: 'Install Date', type: 'date' },
  { key: 'lastMaintenanceDate', label: 'Last Maintenance', type: 'date' },
  { key: 'circuits', label: 'Circuits' },
];

export default function PublicPanelPage() {
  const { qrToken } = useParams();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/public/review/electrical-panel/${qrToken}`);
        setPanel(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Panel not found' : 'Failed to load panel');
      } finally { setLoading(false); }
    };
    load();
  }, [qrToken]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-slate-300" /><span className="text-sm font-semibold text-slate-700">Specard Inc.</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-700">{error}</h2>
          <p className="text-sm text-slate-400 mt-1">The QR code may be invalid or the panel has been removed.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-panel-page">
      <PublicQrPageHeader
        title="Panel Card"
        tagLabel="Panel Name"
        tagValue={panel.name}
        qrToken={panel.qrToken}
        data={panel}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-2">
        {/* Panel Summary */}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-panel-summary">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Panel Information</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            {SUMMARY_FIELDS.map((f) => {
              const raw = panel[f.key];
              const val = f.type === 'date' ? fmtDate(raw) : (raw === null || raw === undefined || raw === '' ? '-' : String(raw));
              return (
                <div key={f.key} data-testid={`public-${f.key}`}>
                  <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{f.label}</dt>
                  <dd className="text-sm text-slate-900 font-medium mt-0.5">{val}</dd>
                </div>
              );
            })}
          </div>
          {panel.notes && (
            <div className="mt-3 pt-3 border-t" data-testid="public-notes">
              <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Notes</dt>
              <dd className="text-sm text-slate-900 mt-0.5 whitespace-pre-wrap">{panel.notes}</dd>
            </div>
          )}
        </div>

        {/* Circuit Schedule */}
        <PublicCircuitSchedule panel={panel} circuits={panel.circuitEntries || []} />

        {/* Maintenance History */}
        <PublicMaintenanceHistory records={panel.maintenanceHistory || []} />

        <p className="text-center text-xs text-slate-400 mt-6">This is a read-only panel card generated by Specard</p>
      </div>
    </div>
  );
}


/* ── Read-only circuit schedule table ── */
function PublicCircuitSchedule({ panel, circuits }) {
  const totalCircuits = panel?.circuits || 0;
  const totalRows = Math.ceil(totalCircuits / 2);

  const rows = useMemo(() => {
    const result = [];
    for (let r = 0; r < totalRows; r++) {
      const lN = r * 2 + 1;
      const rN = r * 2 + 2;
      result.push({
        left: circuits.find(c => c.circuitNumber === lN) || { circuitNumber: lN },
        right: rN <= totalCircuits ? (circuits.find(c => c.circuitNumber === rN) || { circuitNumber: rN }) : null,
      });
    }
    return result;
  }, [circuits, totalRows, totalCircuits]);

  const totals = useMemo(() => {
    const t = { a: 0, b: 0, c: 0, single: 0 };
    circuits.forEach(c => {
      t.a += c.kvaLoadA || 0;
      t.b += c.kvaLoadB || 0;
      t.c += c.kvaLoadC || 0;
      t.single += c.kvaLoad || 0;
    });
    return t;
  }, [circuits]);

  if (totalCircuits === 0) return null;

  const cellCls = (filled) => filled ? 'text-slate-900' : 'text-slate-300';

  const renderLeft = (circ) => {
    const f = !!(circ.deviceTag || circ.equipmentTag || circ.breakerTrip);
    return (
      <>
        <td className={`px-2 py-2.5 text-left ${cellCls(f)}`}>{circ.room || '-'}</td>
        <td className={`px-1 py-2.5 text-left ${cellCls(f)}`}>{circ.deviceTag || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.equipmentTag || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.quantity != null ? circ.quantity : '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{fmtKva(circ.kvaLoad)}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.breakerTrip || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.poles || '-'}</td>
      </>
    );
  };

  const renderRight = (circ) => {
    if (!circ) {
      return <td colSpan={7} className="bg-slate-50/50" />;
    }
    const f = !!(circ.deviceTag || circ.equipmentTag || circ.breakerTrip);
    return (
      <>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.poles || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.breakerTrip || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{fmtKva(circ.kvaLoad)}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.quantity != null ? circ.quantity : '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.equipmentTag || '-'}</td>
        <td className={`px-1 py-2.5 text-right ${cellCls(f)}`}>{circ.deviceTag || '-'}</td>
        <td className={`px-2 py-2.5 text-right ${cellCls(f)}`}>{circ.room || '-'}</td>
      </>
    );
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 overflow-hidden mb-4" data-testid="public-circuit-schedule">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Circuit Schedule</h2>
      <div className="overflow-x-auto border rounded-lg overflow-hidden">
        <table className="w-full border-collapse text-xs" style={{ minWidth: 1060 }}>
          <thead>
            <tr className="bg-slate-100 border-b">
              <th className="px-2 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Location</th>
              <th className="px-1 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Device Tag</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Equip Tag</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Poles</th>
              <th className="px-1 py-2.5 text-center bg-slate-500 text-white font-bold text-xs">CKT</th>
              <th className="bg-slate-500" style={{ width: 18 }} />
              <th className="px-1 py-2.5 text-center bg-slate-500 text-white font-bold text-xs">CKT</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Poles</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Equip Tag</th>
              <th className="px-1 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Device Tag</th>
              <th className="px-2 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Location</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={`border-b border-slate-100 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                {renderLeft(row.left)}
                <td className="text-center font-bold bg-slate-500 text-white text-xs">{row.left.circuitNumber}</td>
                <td className="bg-slate-500">
                  <div className="flex justify-center">
                    <span className="w-1.5 h-3 rounded-sm bg-orange-400" />
                  </div>
                </td>
                <td className="text-center font-bold bg-slate-500 text-white text-xs">{row.right?.circuitNumber || ''}</td>
                {renderRight(row.right)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-xs">
              <td colSpan={4} className="px-2 py-2.5 text-right text-slate-600">TOTAL KVA</td>
              <td className="px-1 py-2.5 text-center text-slate-900">{totals.single.toFixed(1)}</td>
              <td colSpan={2} />
              <td colSpan={3} className="text-center bg-slate-500 text-orange-300 font-bold">
                Total: {totals.single.toFixed(1)}
              </td>
              <td colSpan={2} />
              <td className="px-1 py-2.5 text-center text-slate-900">{totals.single.toFixed(1)}</td>
              <td colSpan={4} className="px-2 py-2.5 text-left text-slate-600">TOTAL KVA</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}


/* ── Maintenance History for Public Panel Page ── */
const formatDateUS = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-');
  return (y && m && d) ? `${m}.${d}.${y}` : dateStr;
};

function PublicMaintenanceHistory({ records }) {
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  if (!records || records.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6" data-testid="public-maintenance-history">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-orange-500" />Maintenance History
        <span className="text-xs font-normal text-slate-400 ml-1">({records.length} record{records.length !== 1 ? 's' : ''})</span>
      </h3>
      <div className="space-y-4">
        {records.map((record, idx) => (
          <div key={record.id || idx} className={`rounded-lg border p-4 ${idx === 0 ? 'border-orange-200 bg-orange-50/30' : 'border-slate-100 bg-slate-50/30'}`} data-testid={`public-maint-record-${idx}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />Date</dt>
                <dd className="text-sm text-slate-800 font-medium">{formatDateUS(record.date)}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1"><Building2 className="h-2.5 w-2.5" />Company</dt>
                <dd className="text-sm text-slate-800">{record.companyName || '-'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1"><User className="h-2.5 w-2.5" />Technician</dt>
                <dd className="text-sm text-slate-800">{record.technician || '-'}</dd>
              </div>
            </div>
            {record.action && (
              <div className="mb-2">
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-0.5">Action</dt>
                <dd className="text-sm text-slate-700 whitespace-pre-wrap">{record.action}</dd>
              </div>
            )}
            {record.photos?.length > 0 && (
              <div className="mb-2">
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1"><ImageIcon className="h-2.5 w-2.5" />Photos ({record.photos.length})</dt>
                <div className="flex flex-wrap gap-2">
                  {record.photos.map(p => (
                    <button key={p.id} type="button" className="w-16 h-16 rounded-lg overflow-hidden border hover:ring-2 ring-orange-400 transition-all cursor-pointer" onClick={() => setEnlargedPhoto(p.url)} data-testid={`public-maint-photo-${p.id}`}>
                      <img src={p.thumbnail || p.url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {record.notes && (
              <div>
                <dt className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-0.5 flex items-center gap-1"><StickyNote className="h-2.5 w-2.5" />Notes</dt>
                <dd className="text-sm text-slate-600 whitespace-pre-wrap">{record.notes}</dd>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Photo Enlarge Modal */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogHeader><DialogTitle className="sr-only">Photo</DialogTitle></DialogHeader>
          {enlargedPhoto && <img src={enlargedPhoto} alt="" className="w-full h-auto max-h-[80vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
